/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnDestroy, type OnInit, type SimpleChanges, Component, ElementRef, Input } from '@angular/core';
import { type Polling, type Rest, EventService, ExtendService, HttpService, LazyloadService, PollingService, UNPLACEHOLDER } from '@dm/core';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { VstoreIdE } from 'app/common/constants/vstore';
import { type CIFSShare, type NFSShare } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config/switch';
import { CifsShareService } from 'app/feature/service/file-service/share/cifs-share/cifs-share.service';
import { DetailCifsShareComponent } from 'app/feature/service/file-service/share/cifs-share/detail-cifs-share/detail-cifs-share.component';
import { ShareMoTypeE } from 'app/feature/service/file-service/share/extension-filtering/extension-filtering-str.service';
import { DetailNfsShareComponent } from 'app/feature/service/file-service/share/nfs-share/detail-nfs-share/detail-nfs-share.component';
import { NfsShareService } from 'app/feature/service/file-service/share/nfs-share/nfs-share.service';
import { ShareTypeE } from 'app/feature/service/file-service/share/share.enum';
import { delay } from 'lodash';
import { type Observable, type Subscription, of } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { OBJECT_QUERY_RANGE, PermissionCheckObjE } from '../smart-assistant.const';
import { SmartAssistantService } from '../smart-assistant.service';
import { SmartAssistantFileShareStrService } from './file-share-str.service';

@Component({
    selector: 'app-smart-assistant-file-share',
    templateUrl: './file-share.component.html',
    styleUrls: ['./file-share.component.scss'],
})
export class SmartAssistantFileShareComponent implements OnInit, OnChanges, OnDestroy {
    /**
     * 是否展示动图区域
     */
    @Input() showImage = true;
    /**
     * 最大高度
     */
    @Input() maxHeight = '100%';
    /**
     * 国际化
     */
    i18n = this.str.i18n;
    /**
     * 文件共享标题
     */
    subTitle: string;
    /**
     * NFS对象
     */
    nfsDatas = [];
    /**
     * CIFS对象
     */
    cifsDatas = [];
    /**
     * 是否处于加载中
     */
    loading = true;
    /**
     * 监听是否创建/删除了NFS共享
     */
    nfsShare$: Subscription;
    /**
     * 监听是否创建/删除了CIFS共享
     */
    cifsShare$: Subscription;
    /**
     * NFS轮询
     */
    nfsPolling$: Polling;
    /**
     * NFS轮询的订阅
     */
    nfsSub$: Subscription;
    /**
     * CIFS轮询
     */
    cifsPolling$: Polling;
    /**
     * CIFS轮询的订阅
     */
    cifsSub$: Subscription;
    /**
     * 有共享的“写”权限
     */
    hasShareWritePermission: boolean;
    /**
     * 是否是折叠状态（首页使用）
     */
    isCollapse = false;
    ProductConfig = ProductConfig;

    constructor(
        public str: SmartAssistantFileShareStrService,
        public el: ElementRef,
        private http: HttpService,
        private ext: ExtendService,
        private nfsShareService: NfsShareService,
        private cifsShareService: CifsShareService,
        private eventService: EventService,
        private pollingService: PollingService,
        private lazyloadService: LazyloadService,
        private smartAssistantService: SmartAssistantService,
    ) { }

    ngOnInit(): void {
        // 没有“共享写”权限，就不展示下面的对象了
        this.hasShareWritePermission = this.smartAssistantService.checkPermission(PermissionCheckObjE.FileShare);
        if (!this.hasShareWritePermission) {
            this.loading = false;
            return;
        }

        this.ext.forkAll([
            this.handleData(this.getNfsRest(), ShareMoTypeE.NFS),
            this.handleData(this.getCifsRest(), ShareMoTypeE.CIFS),
        ]).pipe(
            finalize(() => {
                this.loading = false;
                delay(this.setPolling, 15000);
            }),
        ).subscribe();

        if (!this.nfsShare$) {
            this.nfsShare$ = this.eventService.subscribe(
                EventServiceKeyE.NfsChanged,
                () => this.handleData(this.getNfsRest(), ShareMoTypeE.NFS).subscribe(),
            );
        }

        if (!this.cifsShare$) {
            this.cifsShare$ = this.eventService.subscribe(
                EventServiceKeyE.CifsChanged,
                () => this.handleData(this.getCifsRest(), ShareMoTypeE.CIFS).subscribe(),
            );
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { showImage } = changes;
        if (showImage && showImage.currentValue !== showImage.previousValue) {
            this.isCollapse = !showImage.currentValue;
        }
    }

    getNfsRest(): Rest {
        // 由于共享没有“创建时间”，因此用ID倒序
        return this.nfsShareService.getBatchRest(VstoreIdE.sys).setParams({
            sortby: 'ID,d',
            range: OBJECT_QUERY_RANGE,
        });
    }

    getCifsRest(): Rest {
        // subType分为普通共享、homedir和所有共享，这里查所有共享
        const rest = this.cifsShareService.getBatchRest(VstoreIdE.sys).setParams({
            sortby: 'ID,d',
            range: OBJECT_QUERY_RANGE,
        });
        rest.setFilters([{
            field: 'subType',
            matchMode: '::',
            value: ShareTypeE.All,
        }]);

        return rest;
    }

    /**
     * 设置NFS/CIFS的轮询
     */
    setPolling = (): void => {
        if (!SWITCH.SUPPORT_NAS) {
            return;
        }

        this.nfsPolling$ = this.pollingService.add(this.getNfsRest(), 15);
        this.nfsSub$ = this.nfsPolling$.subscribe(({ data }: { data: NFSShare[] }) => {
            this.nfsDatas = data || [];
            if (this.nfsDatas.length) {
                // 过滤根目录，根目录无法设置权限
                this.nfsDatas = this.nfsDatas.filter((nfs: NFSShare) => nfs.FSID !== UNPLACEHOLDER);
            }
        });

        this.cifsPolling$ = this.pollingService.add(this.getCifsRest(), 15);
        this.cifsSub$ = this.cifsPolling$.subscribe(({ data }: { data: CIFSShare[] }) => {
            this.cifsDatas = data || [];
        });
    };

    /**
     * 处理返回结果
     * @param data NFS/CIFS的rest
     * @param type NFS/CIFS
     */
    handleData(rest: Rest, type: ShareMoTypeE): Observable<{ data: NFSShare[] } | { data: CIFSShare[] }> {
        if (!SWITCH.SUPPORT_NAS) {
            return undefined as Observable<{ data: NFSShare[] } | { data: CIFSShare[] }>;
        }

        if (type === ShareMoTypeE.NFS) {
            return this.http.get(rest).pipe(
                tap(({ data }: { data: NFSShare[] }) => {
                    this.nfsDatas = data || [];
                    if (this.nfsDatas.length) {
                        // 过滤根目录，根目录无法设置权限
                        this.nfsDatas = this.nfsDatas.filter((nfs: NFSShare) => nfs.FSID !== UNPLACEHOLDER);
                    }
                }),
            );
        }

        return this.http.get(rest).pipe(
            tap(({ data }: { data: CIFSShare[] }) => {
                this.cifsDatas = data || [];
            }),
        );
    }

    /**
     * 给NFS共享添加权限
     */
    nfsAddPermission(nfsData: NFSShare): void {
        this.eventService.emit(EventServiceKeyE.ClickAssistantCreateBtn);
        this.lazyloadService.appendComponent(DetailNfsShareComponent, {
            data: nfsData,
        });
    }

    /**
     * 给CIFS共享添加权限
     */
    cifsAddPermission(cifsData: CIFSShare): void {
        this.eventService.emit(EventServiceKeyE.ClickAssistantCreateBtn);
        this.lazyloadService.appendComponent(DetailCifsShareComponent, {
            data: cifsData,
        });
    }

    ngOnDestroy(): void {
        this.nfsShare$?.unsubscribe();
        this.cifsShare$?.unsubscribe();

        if (this.nfsPolling$) {
            this.pollingService.remove(this.nfsPolling$);
            this.nfsSub$?.unsubscribe();
        }

        if (this.cifsPolling$) {
            this.pollingService.remove(this.cifsPolling$);
            this.cifsSub$?.unsubscribe();
        }
    }
}
